import { supabase } from '@/lib/supabase';
import { kickLinkedListRefresh } from '@/shared/components/Grid/computedValues';
import type { RowPatch } from './rowPatch';
import { TEMPLATES } from './templates';
import type { Json } from '@/lib/database.types';
import type { GridDataFormat } from '@/shared/components/Grid/formats';
import type {
  FoldersNode,
  FoldersNodeOpen,
  FoldersPathStep,
  FoldersSharedNode,
  SheetColumn,
  SheetDocument,
  SheetRow,
  SheetTab,
} from './types';

/**
 * Every Supabase call the Folders module makes.
 *
 * `orgId` is passed in from `useOrg()` and never from a route param: RLS is the
 * enforcement, the filter is only for the planner (AGENTS.md, invariant 3).
 * Nothing here trusts an organization id it was handed by a URL.
 */

const NODE_FIELDS =
  'id, org_id, parent_id, kind, doc_type, name, color, trashed_at, updated_at, created_by';

interface NodeRow {
  id: string;
  org_id: string;
  parent_id: string | null;
  kind: string;
  doc_type: string | null;
  name: string;
  color: string | null;
  trashed_at: string | null;
  updated_at: string;
  created_by: string | null;
}

function toNode(row: NodeRow): FoldersNode {
  return {
    id: row.id,
    orgId: row.org_id,
    parentId: row.parent_id,
    kind: row.kind === 'folder' ? 'folder' : 'document',
    docType: row.doc_type,
    name: row.name,
    color: row.color,
    trashedAt: row.trashed_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
  };
}

/** PostgREST returns a many-to-one embed as an object, but the generated types
 *  describe it as an array often enough that guessing at each call site is a
 *  worse bet than normalising once. */
function embeddedNode(value: NodeRow | NodeRow[] | null): NodeRow | null {
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

/**
 * What is inside one folder, or at the root when `parentId` is null.
 *
 * Folders come first because that is what every file manager does and what the
 * eye expects; `kind` sorts descending because 'folder' > 'document'.
 */
export async function listChildren(
  orgId: string,
  parentId: string | null,
  signal?: AbortSignal,
): Promise<FoldersNode[]> {
  let query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .is('trashed_at', null)
    .order('kind', { ascending: false })
    .order('name', { ascending: true });

  query = parentId === null ? query.is('parent_id', null) : query.eq('parent_id', parentId);

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}

/**
 * Name search across the active organization, for the Home search field.
 *
 * Two escapes, both mandatory. `%` and `_` are LIKE wildcards and supabase-js
 * does not escape them, so someone typing "100%" would otherwise match
 * everything. `,` `.` `:` `(` `)` are PostgREST *filter* syntax rather than URL
 * syntax, so encoding does not save them — `name=ilike.%a,b%` parses as two
 * filters. They become `%`, which reads as "anything here" and is what a person
 * typing punctuation means anyway.
 */
export async function searchNodes(
  orgId: string,
  term: string,
  signal?: AbortSignal,
): Promise<FoldersNode[]> {
  const trimmed = term.trim();
  if (trimmed.length < 2) return [];
  const pattern = trimmed
    .replace(/[\\%_]/g, (char) => `\\${char}`)
    .replace(/[,.:()]/g, '%');

  const query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .is('trashed_at', null)
    .ilike('name', `%${pattern}%`)
    .order('kind', { ascending: false })
    .order('name', { ascending: true })
    .limit(20);

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}

/**
 * The trash.
 *
 * Trashing a folder marks the folder and nothing else, so its contents are
 * neither listed here nor reachable by browsing — they simply come back with
 * it. Marking every descendant would make restoring a folder guess which of
 * its children the user had deleted separately beforehand.
 */
export async function listTrash(orgId: string, signal?: AbortSignal): Promise<FoldersNode[]> {
  const query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .not('trashed_at', 'is', null)
    .order('trashed_at', { ascending: false });

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}

export async function getNode(id: string, signal?: AbortSignal): Promise<FoldersNode | null> {
  const base = supabase.from('folders_nodes').select(NODE_FIELDS).eq('id', id);
  // `abortSignal` belongs to the filter builder: applying it after
  // `maybeSingle()` is a type error, not a style choice.
  const { data, error } = await (signal ? base.abortSignal(signal) : base).maybeSingle();
  if (error) throw error;
  return data ? toNode(data) : null;
}

/**
 * Breadcrumb, root first.
 *
 * The RPC runs with the caller's own permissions, so the trail stops at the
 * first folder they cannot read — someone holding a share on one document sees
 * the document, not a map of the folders around it.
 */
export async function getPath(nodeId: string, signal?: AbortSignal): Promise<FoldersPathStep[]> {
  const query = supabase.rpc('folders_path', { p_node_id: nodeId });
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    kind: row.kind === 'folder' ? ('folder' as const) : ('document' as const),
  }));
}

/** Every folder in the organization, for the move dialog's destination list. */
export async function listFolders(orgId: string, signal?: AbortSignal): Promise<FoldersNode[]> {
  const query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .eq('kind', 'folder')
    .is('trashed_at', null)
    .order('name', { ascending: true });

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}

/** Every spreadsheet document in the organization, for copying tabs across documents. */
export async function listSpreadsheets(orgId: string, signal?: AbortSignal): Promise<FoldersNode[]> {
  const query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .eq('kind', 'document')
    .is('trashed_at', null)
    .order('name', { ascending: true });

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}

export async function listDocuments(
  orgId: string,
  docType: string,
  signal?: AbortSignal,
): Promise<FoldersNode[]> {
  const query = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .eq('kind', 'document')
    .eq('doc_type', docType)
    .is('trashed_at', null)
    .order('name', { ascending: true });
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map(toNode);
}


// ---------------------------------------------------------------------------
// Home — what one person starred, opened, or was given
// ---------------------------------------------------------------------------
// Everything below is scoped to the signed-in user by RLS rather than by a
// filter. `folders_stars_own` and `folders_node_access_own` already restrict
// those tables to the caller's own rows, and repeating the condition here would
// be a second opinion that can drift from the one that actually decides.

interface AccessRow {
  opened_at: string;
  open_count: number;
  folders_nodes: NodeRow | NodeRow[] | null;
}

function toOpens(rows: readonly AccessRow[]): FoldersNodeOpen[] {
  return rows.flatMap((row) => {
    const node = embeddedNode(row.folders_nodes);
    return node
      ? [{ node: toNode(node), openedAt: row.opened_at, openCount: row.open_count }]
      : [];
  });
}

/**
 * Starred, non-trashed nodes, newest star first.
 *
 * `!inner` is load-bearing: with a plain embed PostgREST keeps the star row and
 * nulls the node, so the org and trash filters would remove nothing.
 */
/**
 * The active org's starred nodes, plus starred nodes shared into it from
 * elsewhere (`sharedIds`). Not filtered by org in SQL, because a shared node
 * lives in its owner's org; stars in the user's *other* organizations stay out.
 */
export async function listStarred(
  orgId: string,
  signal?: AbortSignal,
  sharedIds: readonly string[] = [],
): Promise<FoldersNode[]> {
  const shared = new Set(sharedIds);
  const query = supabase
    .from('folders_stars')
    .select(`created_at, folders_nodes!inner(${NODE_FIELDS})`)
    .is('folders_nodes.trashed_at', null)
    .order('created_at', { ascending: false });

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).flatMap((row) => {
    const node = embeddedNode(row.folders_nodes as NodeRow | NodeRow[] | null);
    if (!node) return [];
    const converted = toNode(node);
    return converted.orgId === orgId || shared.has(converted.id) ? [converted] : [];
  });
}

/** Just the ids, so a card fetched by any other query knows which way the star
 *  points. Not org-filtered on purpose: that would need the `!inner` embed and
 *  a far larger payload for a list that is short by nature. */
export async function listStarredIds(signal?: AbortSignal): Promise<ReadonlySet<string>> {
  const query = supabase.from('folders_stars').select('node_id');
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return new Set((data ?? []).map((row) => row.node_id));
}

/** The primary key is a full unique, so `.upsert` works here — unlike
 *  `grid_settings` and its partial indexes (supabase/AGENTS.md). */
export async function starNode(nodeId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('folders_stars')
    .upsert({ node_id: nodeId, user_id: userId }, { onConflict: 'node_id,user_id', ignoreDuplicates: true });
  if (error) throw error;
}

export async function unstarNode(nodeId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('folders_stars')
    .delete()
    .eq('node_id', nodeId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * What this person opened most recently.
 *
 * The org filter sits on `folders_node_access.org_id`, not on the embedded
 * node, so `folders_node_access_recent_idx` serves the sort and the limit stops
 * early. `docType` narrows it for the Sheets index, which wants sheets only.
 */
export async function listRecentlyOpened(
  orgId: string,
  limit: number,
  docType?: string,
  signal?: AbortSignal,
  /** Nodes from other organizations to include anyway — documents shared
   *  directly with this person, which live outside the active org. */
  alsoNodeIds: readonly string[] = [],
): Promise<FoldersNodeOpen[]> {
  let query = supabase
    .from('folders_node_access')
    .select(`opened_at, open_count, folders_nodes!inner(${NODE_FIELDS})`);
  query = alsoNodeIds.length
    ? query.or(`org_id.eq.${orgId},node_id.in.(${alsoNodeIds.join(',')})`)
    : query.eq('org_id', orgId);
  query = query
    .eq('folders_nodes.kind', 'document')
    .is('folders_nodes.trashed_at', null)
    .order('opened_at', { ascending: false })
    // Over-fetch: PostgREST counts the limit over top-level rows before the
    // embedded trash filter is applied, so asking for exactly `limit` can come
    // back short.
    .limit(limit + 10);
  if (docType) query = query.eq('folders_nodes.doc_type', docType);

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return toOpens(data as unknown as AccessRow[]).slice(0, limit);
}

/**
 * The documents this person keeps returning to.
 *
 * A floor on the count keeps the section from being "recents in a different
 * order" on a fresh account. `excludeNodeIds` is subtracted in TypeScript so
 * Home can fire this and `listRecentlyOpened` in one `Promise.all` rather than
 * chaining them.
 */
export async function listFrequentlyOpened(
  orgId: string,
  limit: number,
  excludeNodeIds: readonly string[] = [],
  signal?: AbortSignal,
): Promise<FoldersNodeOpen[]> {
  let query = supabase
    .from('folders_node_access')
    .select(`opened_at, open_count, folders_nodes!inner(${NODE_FIELDS})`)
    .eq('org_id', orgId)
    .gte('open_count', 3)
    .eq('folders_nodes.kind', 'document')
    .is('folders_nodes.trashed_at', null)
    .order('open_count', { ascending: false })
    .order('opened_at', { ascending: false })
    .limit(limit + 10);

  // `not.in.()` with an empty list is a PostgREST syntax error, and the ids
  // travel in the URL, so the list is capped rather than sent unbounded.
  const excluded = excludeNodeIds.slice(0, 50);
  if (excluded.length > 0) query = query.not('node_id', 'in', `(${excluded.join(',')})`);

  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return toOpens(data as unknown as AccessRow[]).slice(0, limit);
}

/**
 * Documents shared directly with this person.
 *
 * An RPC because PostgREST can express neither half of it:
 * `resource_shares.resource_id` is polymorphic and carries no foreign key, so
 * `folders_nodes` is not embeddable from it.
 *
 * `orgId` is nullable and Home passes `null`. A share can point at a node in an
 * organization the recipient is not a member of — that is the whole reason
 * `resource_shares` exists apart from `memberships` — so scoping this to the
 * active organization would hide exactly the files the section is for.
 */
export async function listNewSharedWithMe(
  orgId: string | null,
  limit = 12,
  signal?: AbortSignal,
): Promise<FoldersSharedNode[]> {
  // `exactOptionalPropertyTypes` forbids passing an explicit `undefined`, so the
  // org-wide call omits the argument entirely and lets the RPC default apply.
  const query = orgId
    ? supabase.rpc('folders_new_shared_with_me', { p_org_id: orgId, p_limit: limit })
    : supabase.rpc('folders_new_shared_with_me', { p_limit: limit });
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map((row) => ({ node: toNode(row), sharedAt: row.shared_at, openedAt: row.opened_at }));
}

/**
 * Every document of these types this person can open: the active org's, plus
 * those shared directly with them from any organization. Module addresses use
 * it to skip their index when there is only one.
 *
 * Documents reached only through a shared *folder* are not counted; the share
 * RPC returns the shared node itself, not what sits under it.
 */
export async function listAccessibleDocuments(
  orgId: string,
  docTypes: readonly string[],
  signal?: AbortSignal,
): Promise<FoldersNode[]> {
  const own = supabase
    .from('folders_nodes')
    .select(NODE_FIELDS)
    .eq('org_id', orgId)
    .eq('kind', 'document')
    .in('doc_type', [...docTypes])
    .is('trashed_at', null);
  const [ownResult, shared] = await Promise.all([
    signal ? own.abortSignal(signal) : own,
    listNewSharedWithMe(null, 1000, signal),
  ]);
  if (ownResult.error) throw ownResult.error;
  const byId = new Map<string, FoldersNode>();
  for (const node of (ownResult.data ?? []).map(toNode)) byId.set(node.id, node);
  for (const { node } of shared) {
    if (node.kind === 'document' && node.docType && docTypes.includes(node.docType)) byId.set(node.id, node);
  }
  return [...byId.values()];
}

/**
 * Record that this person opened a node.
 *
 * The RPC takes only the id: it reads `org_id` off the node and the user from
 * the JWT, so neither can be asserted by the caller. Failures are swallowed
 * deliberately — open history is an enhancement, and a document must never fail
 * to open because a bookkeeping write did.
 */
export async function recordNodeOpen(nodeId: string): Promise<void> {
  const { error } = await supabase.rpc('folders_record_open', { p_node: nodeId });
  if (error && import.meta.env.DEV) console.warn('folders: open not recorded', error);
}

export interface FoldersOrgMember {
  readonly userId: string;
  readonly fullName: string | null;
  readonly email: string;
}

/** Names for `created_by`. People outside the organization deliberately do not
 *  appear, so a document created by an outside collaborator resolves to no name. */
export async function listOrgMembers(
  orgId: string,
  signal?: AbortSignal,
): Promise<FoldersOrgMember[]> {
  const query = supabase.rpc('org_directory', { p_org_id: orgId });
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map((row) => ({
    userId: row.user_id,
    fullName: row.full_name,
    email: row.email ?? '',
  }));
}

/** Names for the creators of documents shared directly with this person —
 *  the people `org_directory` cannot name because they are in another org. */
export async function listSharedOwnerNames(
  signal?: AbortSignal,
): Promise<{ userId: string; fullName: string | null }[]> {
  const query = supabase.rpc('folders_shared_owner_names');
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map((row) => ({ userId: row.user_id, fullName: row.full_name }));
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export async function createFolder(
  orgId: string,
  parentId: string | null,
  name: string,
  createdBy: string | null,
): Promise<FoldersNode> {
  const { data, error } = await supabase
    .from('folders_nodes')
    .insert({ org_id: orgId, parent_id: parentId, kind: 'folder', name, created_by: createdBy })
    .select(NODE_FIELDS)
    .single();

  if (error) throw error;
  return toNode(data);
}

/**
 * A document is two rows: the node and the Sheet behind it, sharing one id.
 *
 * There is no transaction across two PostgREST calls, so a failure on the
 * second would leave a node with no content. Rather than leave that lying
 * around, the node is removed again — and the editor also upserts a missing
 * `sheet_documents` row when it opens, so even a crash between the two calls
 * heals instead of producing a file that cannot be opened.
 */
export async function createDocument(
  orgId: string,
  parentId: string | null,
  docType: string,
  name: string,
  tabName: string,
  columns: readonly SheetColumn[],
  initialRows: number,
  createdBy: string | null,
  additionalTabs?: readonly { name: string; columns: readonly SheetColumn[] }[],
): Promise<FoldersNode> {
  const { data, error } = await supabase
    .from('folders_nodes')
    .insert({
      org_id: orgId,
      parent_id: parentId,
      kind: 'document',
      doc_type: docType,
      name,
      created_by: createdBy,
    })
    .select(NODE_FIELDS)
    .single();

  if (error) throw error;
  const node = toNode(data);

  const { error: documentError } = await supabase.from('sheet_documents').insert({
    id: node.id,
    org_id: orgId,
    columns: columns as unknown as Json,
    created_by: createdBy,
  });

  if (documentError) {
    await supabase.from('folders_nodes').delete().eq('id', node.id);
    throw documentError;
  }

  const { data: tabData, error: tabError } = await supabase
    .from('sheet_tabs')
    .insert({
      org_id: orgId,
      document_id: node.id,
      name: tabName,
      position: 1,
      columns: columns as unknown as Json,
      created_by: createdBy,
    })
    .select('id')
    .single();
  if (tabError) {
    await supabase.from('folders_nodes').delete().eq('id', node.id);
    throw tabError;
  }

  if (additionalTabs?.length) {
    const { error: extraError } = await supabase.from('sheet_tabs').insert(additionalTabs.map((tab, i) => ({
      org_id: orgId,
      document_id: node.id,
      name: tab.name,
      position: i + 2,
      columns: tab.columns as unknown as Json,
      created_by: createdBy,
    })));
    if (extraError) {
      await supabase.from('folders_nodes').delete().eq('id', node.id);
      throw extraError;
    }
  }

  if (initialRows > 0 && tabData) {
    const rows = Array.from({ length: initialRows }, (_, i) => ({
      org_id: orgId,
      document_id: node.id,
      tab_id: tabData.id,
      position: i + 1,
      data: {} as unknown as Json,
      created_by: createdBy,
    }));
    const { error: rowsError } = await supabase.from('sheet_rows').insert(rows);
    if (rowsError) {
      await supabase.from('folders_nodes').delete().eq('id', node.id);
      throw rowsError;
    }
  }

  return node;
}

export async function renameNode(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('folders_nodes').update({ name }).eq('id', id);
  if (error) throw error;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('folders:rename-node', { detail: { id, name } }));
  }
}

/** The parent trigger refuses a cycle, a document as a parent, and a folder
 *  from another organization. None of that is re-checked here — it would be a
 *  second opinion that can drift from the one that matters. */
export async function moveNode(id: string, parentId: string | null): Promise<void> {
  const { error } = await supabase.from('folders_nodes').update({ parent_id: parentId }).eq('id', id);
  if (error) throw error;
}

export async function trashNode(id: string): Promise<void> {
  const { error } = await supabase
    .from('folders_nodes')
    .update({ trashed_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreNode(id: string): Promise<void> {
  const { error } = await supabase.from('folders_nodes').update({ trashed_at: null }).eq('id', id);
  if (error) throw error;
}

/** Irreversible: the cascade takes the Sheet, its rows and anything nested. */
export async function deleteNode(id: string): Promise<void> {
  const { error } = await supabase.from('folders_nodes').delete().eq('id', id);
  if (error) throw error;
}

/** Irreversibly remove every trashed node in the active organization. */
export async function emptyTrash(orgId: string): Promise<void> {
  const { error } = await supabase
    .from('folders_nodes')
    .delete()
    .eq('org_id', orgId)
    .not('trashed_at', 'is', null);
  if (error) throw error;
}

/**
 * Copy a document with its columns and its rows.
 *
 * Folders are deliberately not duplicable: a deep copy of a tree is a different
 * feature with its own failure modes, and offering it as the same menu item
 * would make one of the two behave surprisingly.
 */
export async function duplicateDocument(
  node: FoldersNode,
  name: string,
  createdBy: string | null,
): Promise<FoldersNode> {
  const originalTabs = (await listSheetTabs(node.id)).sort((x, y) => x.position - y.position);
  const first = originalTabs[0];
  const originals = await Promise.all(originalTabs.map(async (tab) => ({ tab, rows: await listSheetRows(node.id, tab.id) })));
  const copy = await createDocument(node.orgId, node.parentId, node.docType ?? 'sheet', name,
    first?.name ?? name, first?.columns ?? [], 0, createdBy, originalTabs.slice(1));
  try {
    const tabs = (await listSheetTabs(copy.id)).sort((x, y) => x.position - y.position);
    // Reference columns (a child row's `destinationId`, a flow's `accountId`) hold row
    // ids, so every row gets a new id up front and every value naming an old one follows.
    const identities = new Map(originals.flatMap(({ rows }) => rows.map((row) => [row.id, crypto.randomUUID()] as const)));
    const records = originals.flatMap(({ rows }, tabIndex) => rows.map((row) => ({
      id: identities.get(row.id)!, org_id: node.orgId, document_id: copy.id,
      tab_id: tabs[tabIndex]!.id, position: row.position, created_by: createdBy,
      data: Object.fromEntries(Object.entries(row.data).map(([key, value]) => [key,
        typeof value === 'string' ? identities.get(value) ?? value : value,
      ])) as Json,
    })));
    for (let i = 0; i < records.length; i += ROW_BATCH) {
      const { error } = await supabase.from('sheet_rows').insert(records.slice(i, i + ROW_BATCH));
      if (error) throw error;
    }
    return copy;
  } catch (error) {
    await supabase.from('folders_nodes').delete().eq('org_id', node.orgId).eq('id', copy.id);
    throw error;
  }
}


// ---------------------------------------------------------------------------
// The Sheet behind a document
// ---------------------------------------------------------------------------

const CELL_TYPES: readonly string[] = ['text', 'number', 'date', 'chip', 'smartchip', 'autofill', 'checkbox', 'text_document', 'pivot_table', 'reconciliation'];

const DATA_TYPES: readonly string[] = ['auto', 'text', 'date', 'number', 'smartchip', 'autofill', 'rollup', 'lookup', 'attachment', 'barcode', 'checkbox'];

/** A stored `dataFormat` is whatever was written into the column list, so its shape is checked. */
function isGridDataFormat(value: unknown): value is GridDataFormat {
  return Boolean(value) && typeof value === 'object' && DATA_TYPES.includes((value as { type?: unknown }).type as string);
}

/**
 * Narrow a jsonb blob to columns without trusting its shape. The column list is
 * the one piece of a document the grid cannot recover from if it is malformed,
 * so anything unrecognisable is dropped rather than passed along.
 */
function toColumns(value: unknown): SheetColumn[] {
  if (!Array.isArray(value)) return [];

  const columns: SheetColumn[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const { id, name, cellType, width, dataFormat, multiline, readOnly } = item as Record<string, unknown>;
    if (typeof id !== 'string' || typeof name !== 'string') continue;

    const cell =
      typeof cellType === 'string' && CELL_TYPES.includes(cellType)
        ? (cellType as 'text' | 'number' | 'date' | 'chip' | 'smartchip' | 'autofill' | 'checkbox' | 'text_document' | 'pivot_table' | 'reconciliation')
        : null;
    const size = typeof width === 'number' && Number.isFinite(width) ? width : null;
    // A stored column carries how it is meant to be read - currency, a date style, a
    // read-only derived value. Dropping those here silently undid what a template
    // declared: a Trips document loaded its totals as bare numbers, and a column copied
    // with its format lost it on the way back in.
    const format = isGridDataFormat(dataFormat) ? dataFormat : null;

    // Spread rather than assign: `exactOptionalPropertyTypes` treats "absent"
    // and "present but undefined" as different, and an optional column type has
    // to be the first one.
    columns.push({
      id,
      name,
      ...(cell ? { cellType: cell } : {}),
      ...(size !== null ? { width: size } : {}),
      ...(format ? { dataFormat: format } : {}),
      ...(multiline === true ? { multiline: true } : {}),
      ...(readOnly === true ? { readOnly: true } : {}),
    });
  }
  return columns;
}

export async function getSheetDocument(
  id: string,
  signal?: AbortSignal,
): Promise<SheetDocument | null> {
  const base = supabase.from('sheet_documents').select('id, org_id, columns, is_public').eq('id', id);
  const { data, error } = await (signal ? base.abortSignal(signal) : base).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { id: data.id, orgId: data.org_id, columns: toColumns(data.columns), isPublic: data.is_public };
}

export async function listSheetTabs(documentId: string, signal?: AbortSignal): Promise<SheetTab[]> {
  const query = supabase.from('sheet_tabs').select('id, document_id, name, position, columns, is_hidden, columns_link_id').eq('document_id', documentId).order('position');
  const { data, error } = signal ? await query.abortSignal(signal) : await query;
  if (error) throw error;
  return (data ?? []).map((tab) => ({ id: tab.id, documentId: tab.document_id, name: tab.name, position: Number(tab.position), columns: toColumns(tab.columns), isHidden: Boolean(tab.is_hidden), columnsLinkId: tab.columns_link_id ?? null }));
}

export async function createSheetTab(
  orgId: string,
  documentId: string,
  name: string,
  columns: readonly SheetColumn[],
  createdBy: string | null,
  initialRows = 0,
  /** Rows to start with, written in order; takes the place of `initialRows` when given. */
  initialData: readonly Record<string, unknown>[] = [],
): Promise<SheetTab> {
  const { data, error } = await supabase
    .from('sheet_tabs')
    .insert({
      org_id: orgId,
      document_id: documentId,
      name,
      position: Date.now(),
      columns: columns as unknown as Json,
      created_by: createdBy,
    })
    .select('id, document_id, name, position, columns, is_hidden, columns_link_id')
    .single();
  if (error) throw error;

  const rowCount = initialData.length || initialRows;
  if (rowCount > 0) {
    const rows = Array.from({ length: rowCount }, (_, i) => ({
      org_id: orgId,
      document_id: documentId,
      tab_id: data.id,
      position: i + 1,
      data: (initialData[i] ?? {}) as unknown as Json,
      created_by: createdBy,
    }));
    const { error: rowsError } = await supabase.from('sheet_rows').insert(rows);
    if (rowsError) {
      await supabase.from('sheet_tabs').delete().eq('id', data.id);
      throw rowsError;
    }
  }

  return {
    id: data.id,
    documentId: data.document_id,
    name: data.name,
    position: Number(data.position),
    columns: toColumns(data.columns),
    isHidden: Boolean(data.is_hidden),
    columnsLinkId: data.columns_link_id ?? null,
  };
}

export async function renameSheetTab(tabId: string, name: string): Promise<void> {
  const { error } = await supabase.from('sheet_tabs').update({ name }).eq('id', tabId);
  if (error) throw error;
}

export async function deleteSheetTab(tabId: string): Promise<void> {
  const { error } = await supabase.from('sheet_tabs').delete().eq('id', tabId);
  if (error) throw error;
}

export async function setSheetTabHidden(tabId: string, isHidden: boolean): Promise<void> {
  const { error } = await supabase.from('sheet_tabs').update({ is_hidden: isHidden }).eq('id', tabId);
  if (error) throw error;
}

export async function reorderSheetTabs(documentId: string, orderedTabIds: string[]): Promise<void> {
  const updates = orderedTabIds.map((id, index) =>
    supabase.from('sheet_tabs').update({ position: index + 1 }).eq('id', id).eq('document_id', documentId),
  );
  const results = await Promise.all(updates);
  for (const res of results) {
    if (res.error) throw res.error;
  }
}

export interface DuplicateSheetTabOptions {
  readonly headersOnly?: boolean | undefined;
  readonly initialRows?: number | undefined;
  /**
   * Columns and rows to copy in place of the source tab's stored ones, for a tab whose
   * grid is not made of sheet rows: the investments's own tab keeps a placeholder column
   * list and holds its data in `investments_positions`, so copying what is stored would
   * produce an empty sheet with the wrong headers. Everything else about the duplicate -
   * grid settings, formulas, widths - is copied the same way either source is used.
   */
  readonly columns?: readonly SheetColumn[] | undefined;
  readonly rows?: readonly Readonly<Record<string, unknown>>[] | undefined;
}

export async function duplicateSheetTab(
  orgId: string,
  sourceDocumentId: string,
  targetDocumentId: string,
  sourceTabId: string,
  newTabName: string,
  createdBy: string | null,
  options?: DuplicateSheetTabOptions | undefined,
): Promise<SheetTab> {
  const sourceTabs = await listSheetTabs(sourceDocumentId);
  const sourceTab = sourceTabs.find((t) => t.id === sourceTabId);
  if (!sourceTab) throw new Error('Source tab not found');

  const headersOnly = options?.headersOnly ?? false;
  const suppliedRows = options?.rows?.map((data, index): SheetRow => ({ id: `supplied:${index}`, position: index + 1, data }));
  const sourceRows = headersOnly ? [] : suppliedRows ?? await listSheetRows(sourceDocumentId, sourceTabId);

  const sourceGridKey = `sheet:${sourceTabId}`;

  // Fetch existing header overrides for sourceTab so the new tab's columns
  // have the effective renamed header names directly in sheet_tabs.columns
  const { data: headerSettings } = await supabase
    .from('grid_settings')
    .select('value')
    .eq('org_id', orgId)
    .eq('document_id', sourceDocumentId)
    .eq('key', 'headers')
    .maybeSingle();

  const sourceHeaderOverrides = (headerSettings?.value as Record<string, Record<string, string>> | null)?.[sourceGridKey] ?? {};
  const effectiveColumns = (options?.columns ?? sourceTab.columns).map((col) => ({
    ...col,
    name: sourceHeaderOverrides[col.id] ?? col.name,
  }));

  const { data: newTab, error: tabError } = await supabase
    .from('sheet_tabs')
    .insert({
      org_id: orgId,
      document_id: targetDocumentId,
      name: newTabName,
      position: Date.now(),
      columns: effectiveColumns as unknown as Json,
      created_by: createdBy,
    })
    .select('id, document_id, name, position, columns, is_hidden, columns_link_id')
    .single();
  if (tabError) throw tabError;

  let newRows: Array<{ id: string; position: number }> = [];
  if (headersOnly) {
    const initialRowsCount = options?.initialRows ?? TEMPLATES.sheet.initialRows;
    if (initialRowsCount > 0) {
      const emptyRows = Array.from({ length: initialRowsCount }, (_, i) => ({
        org_id: orgId,
        document_id: targetDocumentId,
        tab_id: newTab.id,
        position: i + 1,
        data: {} as unknown as Json,
        created_by: createdBy,
      }));
      const { data: insertedRows, error: rowsError } = await supabase
        .from('sheet_rows')
        .insert(emptyRows)
        .select('id, position');
      if (rowsError) {
        await supabase.from('sheet_tabs').delete().eq('id', newTab.id);
        throw rowsError;
      }
      newRows = (insertedRows ?? []).map((row) => ({ id: row.id, position: Number(row.position) }));
    }
  } else if (sourceRows.length > 0) {
    const rowsToInsert = sourceRows.map((r, index) => ({
      org_id: orgId,
      document_id: targetDocumentId,
      tab_id: newTab.id,
      position: r.position || index + 1,
      data: r.data as unknown as Json,
      created_by: createdBy,
    }));
    const { data: insertedRows, error: rowsError } = await supabase
      .from('sheet_rows')
      .insert(rowsToInsert)
      .select('id, position');
    if (rowsError) {
      await supabase.from('sheet_tabs').delete().eq('id', newTab.id);
      throw rowsError;
    }
    newRows = (insertedRows ?? []).map((row) => ({ id: row.id, position: Number(row.position) }));
  }

  // Clone document-scoped grid settings and overrides as part of the tab copy.
  // The values are keyed by gridKey, so only the copied tab's branch is
  // remapped; unrelated tabs in the same document remain untouched.
  const targetGridKey = `sheet:${newTab.id}`;
  const rowMap = new Map<string, string>();
  sourceRows.forEach((row, index) => {
    const targetId = newRows[index]?.id ?? row.id;
    rowMap.set(row.id, targetId);
    rowMap.set(`id:${row.id}`, `id:${targetId}`);
  });
  const remapCanonicalRows = (text: string): string => {
    let next = text;
    for (const [sourceRowId, targetRowId] of rowMap) {
      next = next.replaceAll(`@'${sourceRowId}'`, `@'${targetRowId}'`);
    }
    return next;
  };
  const { data: settings, error: settingsError } = await supabase
    .from('grid_settings')
    .select('key, value')
    .eq('org_id', orgId)
    .eq('document_id', sourceDocumentId);
  if (settingsError) {
    await supabase.from('sheet_tabs').delete().eq('id', newTab.id);
    throw settingsError;
  }
  const remap = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(remap);
    if (typeof value === 'string') return remapCanonicalRows(value);
    if (!value || typeof value !== 'object') return value;
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key === sourceGridKey ? targetGridKey : key] = remap(item);
    }
    return out;
  };
  if (settings?.length) {
    for (const setting of settings) {
      if (headersOnly && (setting.key === 'row_order' || setting.key === 'row_heights')) continue;
      if (!setting.value || typeof setting.value !== 'object' || Array.isArray(setting.value)) continue;
      const sourceValue = (setting.value as Record<string, unknown>)[sourceGridKey];
      if (sourceValue === undefined) continue;
      const current = await supabase.from('grid_settings').select('value').eq('document_id', targetDocumentId).eq('key', setting.key).maybeSingle();
      if (current.error) { await supabase.from('sheet_tabs').delete().eq('id', newTab.id); throw current.error; }
      const currentValue = current.data?.value && typeof current.data.value === 'object' && !Array.isArray(current.data.value)
        ? current.data.value as Record<string, unknown>
        : {};
      const value = { ...currentValue, [targetGridKey]: remap(sourceValue) } as Json;
      const { error } = current.data
        ? await supabase.from('grid_settings').update({ value }).eq('document_id', targetDocumentId).eq('key', setting.key)
        : await supabase.from('grid_settings').insert({ org_id: orgId, document_id: targetDocumentId, key: setting.key, value, created_by: createdBy });
      if (error) { await supabase.from('sheet_tabs').delete().eq('id', newTab.id); throw error; }
    }
  }
  if (!headersOnly) {
    const { data: overrides, error: overridesError } = await supabase
      .from('cell_overrides')
      .select('grid_key, row_key, field, value')
      .eq('org_id', orgId)
      .eq('document_id', sourceDocumentId)
      .eq('grid_key', sourceGridKey);
    if (overridesError) {
      await supabase.from('sheet_tabs').delete().eq('id', newTab.id);
      throw overridesError;
    }
    if (overrides?.length) {
      const { error } = await supabase.from('cell_overrides').insert(overrides.map((override) => ({
        org_id: orgId,
        document_id: targetDocumentId,
        grid_key: targetGridKey,
        row_key: rowMap.get(override.row_key) ?? override.row_key,
        field: override.field,
        value: remap(override.value) as Json,
        created_by: createdBy,
      })));
      if (error && error.code !== '23505') {
        await supabase.from('sheet_tabs').delete().eq('id', newTab.id);
        throw error;
      }
    }
  }

  return {
    id: newTab.id,
    documentId: newTab.document_id,
    name: newTab.name,
    position: Number(newTab.position),
    columns: toColumns(newTab.columns),
    isHidden: Boolean(newTab.is_hidden),
    columnsLinkId: newTab.columns_link_id ?? null,
  };
}

/** Create the content row for a node that somehow has none. See
 *  `createDocument` — this is the other half of that self-healing. */
export async function ensureSheetDocument(
  node: FoldersNode,
  columns: readonly SheetColumn[],
  createdBy: string | null,
): Promise<SheetDocument> {
  const { data, error } = await supabase
    .from('sheet_documents')
    .upsert(
      {
        id: node.id,
        org_id: node.orgId,
        columns: columns as unknown as Json,
        created_by: createdBy,
      },
      { onConflict: 'id', ignoreDuplicates: true },
    )
    .select('id, org_id, columns, is_public')
    .maybeSingle();

  if (error) throw error;
  if (data) {
    const tabs = await listSheetTabs(node.id);
    if (tabs.length === 0) {
      const { error: tabError } = await supabase.from('sheet_tabs').insert({
        org_id: node.orgId,
        document_id: node.id,
        name: 'Untitled',
        position: 1,
        columns: data.columns,
        created_by: createdBy,
      });
      if (tabError) throw tabError;
    }
    return { id: data.id, orgId: data.org_id, columns: toColumns(data.columns), isPublic: data.is_public };
  }

  const existing = await getSheetDocument(node.id);
  if (!existing) throw new Error('sheet document could not be created');
  const tabs = await listSheetTabs(node.id);
  if (tabs.length === 0) {
    const { error: tabError } = await supabase.from('sheet_tabs').insert({
      org_id: node.orgId,
      document_id: node.id,
      name: 'Untitled',
      position: 1,
      columns: existing.columns as unknown as Json,
      created_by: createdBy,
    });
    if (tabError) throw tabError;
  }
  return existing;
}

export async function saveColumns(
  documentId: string,
  columns: readonly SheetColumn[],
): Promise<void> {
  const { error } = await supabase
    .from('sheet_documents')
    .update({ columns: columns as unknown as Json })
    .eq('id', documentId);
  if (error) throw error;
}

export async function saveTabColumns(tabId: string, columns: readonly SheetColumn[]): Promise<void> {
  const { error } = await supabase.from('sheet_tabs').update({ columns: columns as unknown as Json }).eq('id', tabId);
  if (error) throw error;
}

/**
 * Links `targetTabId`'s columns to `sourceTabId`'s: from then on both tabs hold the
 * same columns (a trigger copies every change). `mapping` moves the target rows'
 * values from its own column ids to the source's, for ids that differ. Returns the
 * link id both tabs now carry.
 */
export async function linkTabColumns(sourceTabId: string, targetTabId: string, mapping: Readonly<Record<string, string>> = {}): Promise<string> {
  const { data, error } = await supabase.rpc('link_sheet_tab_columns', { p_source: sourceTabId, p_target: targetTabId, p_mapping: mapping as unknown as Json });
  if (error) throw error;
  return data;
}

/** Stops a tab sharing its columns; it keeps the columns it has. */
export async function unlinkTabColumns(tabId: string): Promise<void> {
  const { error } = await supabase.from('sheet_tabs').update({ columns_link_id: null }).eq('id', tabId);
  if (error) throw error;
}

export async function listSheetRows(
  documentId: string,
  tabId?: string,
  signal?: AbortSignal,
): Promise<SheetRow[]> {
  const pageSize = 1000;
  const all: Array<{ id: string; position: number | string; data: Json }> = [];
  for (let from = 0; ; from += pageSize) {
    let query = supabase
      .from('sheet_rows')
      .select('id, position, data')
      .eq('document_id', documentId)
      .eq('tab_id', tabId ?? '')
      .order('position', { ascending: true })
      .range(from, from + pageSize - 1);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) throw error;
    all.push(...(data ?? []));
    if ((data?.length ?? 0) < pageSize) break;
  }

  return all.map((row) => ({
    id: row.id,
    position: Number(row.position),
    data:
      row.data && typeof row.data === 'object' && !Array.isArray(row.data)
        ? (row.data as Record<string, unknown>)
        : {},
  }));
}

/**
 * A tab's rows as readers see them: what was typed, with formula results from
 * `computed` laid over it. The stored results are a cache the grid refreshes,
 * so a reader that can evaluate formulas itself should prefer its own values
 * and keep these for what it cannot evaluate.
 */
export async function listSheetRowValues(
  documentId: string,
  tabId: string,
  signal?: AbortSignal,
): Promise<{ id: string; values: Record<string, unknown> }[]> {
  const pageSize = 1000;
  const out: { id: string; values: Record<string, unknown> }[] = [];
  const asObject = (value: Json | null) =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  for (let from = 0; ; from += pageSize) {
    let query = supabase
      .from('sheet_rows')
      .select('id, data, computed')
      .eq('document_id', documentId)
      .eq('tab_id', tabId)
      .order('position', { ascending: true })
      .range(from, from + pageSize - 1);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) throw error;
    for (const row of data ?? []) {
      const merged = { ...asObject(row.data) };
      // Only into empty cells: a typed value under a formula is an input, never its result.
      for (const [field, value] of Object.entries(asObject(row.computed))) {
        const own = merged[field];
        if (value !== null && value !== undefined && (own === null || own === undefined || own === '')) merged[field] = value;
      }
      out.push({ id: row.id, values: merged });
    }
    if ((data?.length ?? 0) < pageSize) break;
  }
  return out;
}

export async function insertRow(
  orgId: string,
  documentId: string,
  tabId: string,
  position: number,
  createdBy: string | null,
  rowData: Record<string, unknown> = {},
  /**
   * Reuse a specific id. Undoing a delete restores the row it removed rather than
   * creating a lookalike, which keeps every history entry naming that row valid.
   */
  id?: string,
): Promise<SheetRow> {
  const { data, error } = await supabase
    .from('sheet_rows')
    .insert({ ...(id ? { id } : {}), org_id: orgId, document_id: documentId, tab_id: tabId, position, data: rowData as Json, created_by: createdBy })
    .select('id, position, data')
    .single();

  if (error) throw error;
  kickLinkedListRefresh();
  return { id: data.id, position: Number(data.position), data: data.data as Record<string, unknown> };
}

export async function insertRows(
  orgId: string,
  documentId: string,
  tabId: string,
  startPosition: number,
  count: number,
  createdBy: string | null,
): Promise<SheetRow[]> {
  const rows = Array.from({ length: count }, (_, i) => ({
    org_id: orgId,
    document_id: documentId,
    tab_id: tabId,
    position: startPosition + i,
    data: {},
    created_by: createdBy,
  }));
  const { data, error } = await supabase
    .from('sheet_rows')
    .insert(rows)
    .select('id, position, data');

  if (error) throw error;
  kickLinkedListRefresh();
  return data.map((d) => ({ id: d.id, position: Number(d.position), data: {} }));
}

/**
 * Saves the cells of one row that changed (see `rowPatch.ts`): the database merges
 * them into the stored row, and a null clears a cell. Throws when the row was not
 * written, which is what a row the user may not edit comes back as.
 */
export async function saveRowData(rowId: string, patch: RowPatch): Promise<void> {
  await saveRowsData([{ id: rowId, patch }]);
}

/** Saves several rows' changed cells in one request (a paste). Throws when any row was
 * not written, naming how many, so the caller does not report a partial save as done. */
export async function saveRowsData(
  rows: ReadonlyArray<{ id: string; patch: RowPatch }>,
): Promise<void> {
  const updates = rows.filter((row) => Object.keys(row.patch).length > 0).map((row) => ({ id: row.id, data: row.patch }));
  if (updates.length === 0) return;
  const { data, error } = await supabase.rpc('save_sheet_rows', { p_updates: updates as unknown as Json });
  if (error) throw error;
  const saved = new Set((data ?? []).map(String));
  const missing = updates.filter((row) => !saved.has(row.id)).length;
  kickLinkedListRefresh();
  if (missing > 0) throw new Error(`${missing} of ${updates.length} rows were not saved`);
}

export async function deleteRow(rowId: string): Promise<void> {
  const { error } = await supabase.from('sheet_rows').delete().eq('id', rowId);
  if (error) throw error;
  kickLinkedListRefresh();
}

/** Ids per request: keeps the `id=in.(…)` filter well inside URL length limits. */
const ROW_BATCH = 200;

async function inBatches(
  ids: readonly string[],
  call: (batch: string[]) => Promise<{ data: string[] | null; error: unknown }>,
): Promise<string[]> {
  const done: string[] = [];
  for (let i = 0; i < ids.length; i += ROW_BATCH) {
    const { data, error } = await call(ids.slice(i, i + ROW_BATCH));
    if (error) {
      if (done.length > 0) kickLinkedListRefresh();
      throw Object.assign(error as object, { doneIds: done });
    }
    done.push(...(data ?? []).map(String));
  }
  if (ids.length > 0) kickLinkedListRefresh();
  return done;
}

/**
 * Moves rows to the sheet's trash in one request (one per 200 ids for larger
 * selections). Returns the ids actually trashed: a row already gone, or one the
 * caller may not edit, is not among them.
 */
export function trashRows(documentId: string, rowIds: readonly string[]): Promise<string[]> {
  return inBatches(rowIds, async (batch) => supabase.rpc('trash_sheet_rows', { p_document: documentId, p_ids: batch }));
}

/** Puts trashed rows back where they were, with their id, position and values. */
export function restoreTrashedRows(documentId: string, rowIds: readonly string[]): Promise<string[]> {
  return inBatches(rowIds, async (batch) => supabase.rpc('restore_sheet_rows', { p_document: documentId, p_ids: batch }));
}

/** Removes rows from the trash for good. */
export function purgeTrashedRows(documentId: string, rowIds: readonly string[]): Promise<string[]> {
  return inBatches(rowIds, async (batch) => {
    const { data, error } = await supabase
      .from('sheet_row_trash')
      .delete()
      .eq('document_id', documentId)
      .in('id', batch)
      .select('id');
    return { data: (data ?? []).map((row) => row.id), error };
  });
}

export interface TrashedSheetRow {
  id: string;
  position: number;
  data: Record<string, unknown>;
  trashedAt: string;
}

/** A tab's trashed rows, most recently trashed first. */
export async function listTrashedRows(documentId: string, tabId: string): Promise<TrashedSheetRow[]> {
  const { data, error } = await supabase
    .from('sheet_row_trash')
    .select('id, position, data, trashed_at')
    .eq('document_id', documentId)
    .eq('tab_id', tabId)
    .order('trashed_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    position: Number(row.position),
    data: (row.data as Record<string, unknown>) ?? {},
    trashedAt: row.trashed_at,
  }));
}

export async function moveSheetRows(
  orgId: string,
  documentId: string,
  targetTabId: string,
  rowIds: string[],
  /** Source column id -> target column id, when the tabs' ids differ. The database renames
   * each row's keys as it moves it, so nothing read here can overwrite a later edit. */
  columnMap?: Readonly<Record<string, string>>,
): Promise<void> {
  if (rowIds.length === 0) return;

  // Find highest position in destination tab to append rows as last rows
  const { data: targetRows, error: maxErr } = await supabase
    .from('sheet_rows')
    .select('position')
    .eq('org_id', orgId)
    .eq('document_id', documentId)
    .eq('tab_id', targetTabId)
    .order('position', { ascending: false })
    .limit(1);

  if (maxErr) throw maxErr;
  let nextPos = (targetRows && targetRows.length > 0 ? Number(targetRows[0]!.position) : 0) + 1;

  const moves = rowIds.map((id) => ({ id, position: nextPos++ }));
  const { data: moved, error } = await supabase.rpc('move_sheet_rows', {
    p_document: documentId,
    p_target_tab: targetTabId,
    p_rows: moves as unknown as Json,
    ...(columnMap ? { p_columns: columnMap as unknown as Json } : {}),
  });
  if (error) throw error;
  const missing = moves.length - (moved ?? []).length;
  if (missing > 0) {
    kickLinkedListRefresh();
    throw new Error(`${missing} of ${moves.length} rows were not moved`);
  }
  kickLinkedListRefresh();
}
